/**
 * Trunnion AI - Agent Chat View
 * Main chat layout with session sidebar and chat area.
 */

import { useEffect, useCallback } from 'react';
import { ChatHeader } from './ChatHeader';
import { ChatInput } from './ChatInput';
import { ChatMessageList } from './ChatMessageList';
import { ChatSessionSidebar } from './ChatSessionSidebar';
import { useChatStore } from '@/stores/chatStore';
import { useChatSessions, useChatHistory, useDeleteChatSession } from '@/hooks/queries/useChat';
import { useChatStream } from '@/hooks/useChatStream';

interface AgentChatViewProps {
  agentId: string;
  agentName: string;
  agentTier?: string;
  modelName?: string;
}

export function AgentChatView({ agentId, agentName, agentTier, modelName }: AgentChatViewProps) {
  const {
    activeSessionId,
    messages,
    isStreaming,
    setActiveSession,
    setMessages,
    clearChat,
  } = useChatStore();

  const { data: sessionsData } = useChatSessions(agentId);
  const { data: historyData } = useChatHistory(agentId, activeSessionId);
  const deleteMutation = useDeleteChatSession();
  const { sendMessage, cancelStream } = useChatStream(agentId);

  // Load messages when session changes
  useEffect(() => {
    if (historyData) {
      setMessages(historyData);
    }
  }, [historyData, setMessages]);

  // Clean up on unmount
  useEffect(() => {
    return () => clearChat();
  }, [clearChat]);

  const handleSelectSession = useCallback(
    (sessionId: string) => {
      setActiveSession(sessionId);
    },
    [setActiveSession],
  );

  const handleNewSession = useCallback(() => {
    setActiveSession(null);
    setMessages([]);
  }, [setActiveSession, setMessages]);

  const handleDeleteSession = useCallback(
    (sessionId: string) => {
      deleteMutation.mutate(sessionId);
      if (sessionId === activeSessionId) {
        handleNewSession();
      }
    },
    [deleteMutation, activeSessionId, handleNewSession],
  );

  const handleSend = useCallback(
    (message: string, classificationLevel: string) => {
      sendMessage(message, classificationLevel);
    },
    [sendMessage],
  );

  return (
    <div className="flex h-full overflow-hidden rounded-xl border border-trunnion-slate-700 bg-trunnion-slate-950">
      {/* Session sidebar */}
      <ChatSessionSidebar
        sessions={sessionsData?.sessions ?? []}
        activeSessionId={activeSessionId}
        onSelectSession={handleSelectSession}
        onNewSession={handleNewSession}
        onDeleteSession={handleDeleteSession}
      />

      {/* Main chat area */}
      <div className="flex flex-1 flex-col min-w-0">
        <ChatHeader
          agentName={agentName}
          agentTier={agentTier}
          modelName={modelName}
        />
        <ChatMessageList messages={messages} />
        <ChatInput
          onSend={handleSend}
          onCancel={cancelStream}
          isStreaming={isStreaming}
        />
      </div>
    </div>
  );
}
